import { Button, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { DEFAULT_LEGEND, PieChartSpec, UnitOptions } from '@/core';
import {
  DECIMAL_PLACES_OPTIONS,
  LegendOptionsEditor,
  LegendOptionsEditorProps,
  OptionsEditorColumn,
  OptionsEditorControl,
  OptionsEditorGrid,
  OptionsEditorGroup,
  SettingsAutocomplete,
  UnitSelector,
} from '@/plugin-editor-components';
import {
  DEFAULT_AGGREGATION_PERCENT,
  DEFAULT_LABEL_FORMAT,
  DEFAULT_UNIT,
  DEFAULT_VISUAL,
  PieChartOptionsEditorProps,
} from './pie-chart-model';
import { convertLabelFormatArrayToString, convertLabelFormatStringToArray, LabelFormatString } from './utils';
import { VisualOptionsEditor, VisualOptionsEditorProps } from './VisualOptionsEditor';

export function PieChartOptionsEditorSettings(props: PieChartOptionsEditorProps) {
  const { onChange, value } = props;

  const handleLegendChange: LegendOptionsEditorProps['onChange'] = (newLegend) => {
    onChange(
      produce(value, (draft: PieChartSpec) => {
        draft.legend = newLegend;
      })
    );
  };

  const handleVisualChange: VisualOptionsEditorProps['onChange'] = (newVisual) => {
    onChange(
      produce(value, (draft: PieChartSpec) => {
        draft.visual = newVisual;
      })
    );
  };

  const handleLabelFormatChange = (_: unknown, label_format: LabelFormatString): void => {
    onChange(
      produce(value, (draft: PieChartSpec) => {
        draft.label_format = convertLabelFormatStringToArray(label_format);
      })
    );
  };

  const handlePercentDecimalPlacesChange = (_: unknown, { decimal_places }: { decimal_places: number | undefined }) => {
    onChange(
      produce(value, (draft: PieChartSpec) => {
        draft.percent_decimal_places = decimal_places;
      })
    );
  };

  const handleUnitChange = (newUnit: UnitOptions) => {
    onChange(
      produce(value, (draft: PieChartSpec) => {
        draft.unit = newUnit;
      })
    );
  };

  const handleAggregationPercentChange = (e: { target: { value: string } }) => {
    const newPercent = e.target.value ? Number(e.target.value) : undefined;
    onChange(
      produce(value, (draft: PieChartSpec) => {
        draft.aggregation_percent = newPercent;
      })
    );
  };

  const handleResetSettings: React.MouseEventHandler<HTMLButtonElement> = () => {
    onChange(
      produce(value, (draft: PieChartSpec) => {
        draft.legend = DEFAULT_LEGEND;
        draft.visual = DEFAULT_VISUAL;
        draft.label_format = DEFAULT_LABEL_FORMAT;
        draft.unit = DEFAULT_UNIT;
        draft.aggregation_percent = DEFAULT_AGGREGATION_PERCENT;
      })
    );
  };

  // convert label format array to string since we can't use an array for the value prop of a ToggleButtonGroup
  const labelFormatString = convertLabelFormatArrayToString(value.label_format);

  return (
    <OptionsEditorGrid>
      <OptionsEditorColumn>
        <OptionsEditorGroup title="Legend">
          <LegendOptionsEditor value={value.legend} onChange={handleLegendChange} hideValuesDropdown />
        </OptionsEditorGroup>
        <VisualOptionsEditor value={value.visual ?? DEFAULT_VISUAL} onChange={handleVisualChange} />
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <OptionsEditorGroup title="Options">
          <OptionsEditorControl
            label="Label format"
            control={
              <ToggleButtonGroup value={labelFormatString} onChange={handleLabelFormatChange}>
                <ToggleButton aria-label="percent" value="Percent">
                  Percent
                </ToggleButton>
                <ToggleButton aria-label="value" value="Value">
                  Value
                </ToggleButton>
                <ToggleButton aria-label="both percent and value" value="Both">
                  Both
                </ToggleButton>
              </ToggleButtonGroup>
            }
          />
          <Typography variant="h4">Percent</Typography>
          <OptionsEditorControl
            label="Decimals"
            control={
              <SettingsAutocomplete
                value={DECIMAL_PLACES_OPTIONS.find((o) => o.decimal_places === value.percent_decimal_places)}
                options={DECIMAL_PLACES_OPTIONS}
                getOptionLabel={(o) => o.label}
                onChange={handlePercentDecimalPlacesChange}
                disableClearable
              />
            }
          />
          <Typography variant="h4">Value</Typography>
          <UnitSelector value={value.unit ?? DEFAULT_UNIT} onChange={handleUnitChange} />
          <OptionsEditorControl
            label="Aggregate when % is <="
            control={
              <TextField
                fullWidth
                type="number"
                value={value.aggregation_percent}
                onChange={handleAggregationPercentChange}
              />
            }
          />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <OptionsEditorGroup title="Reset Settings">
          <Button variant="secondary" onClick={handleResetSettings}>
            Reset to defaults
          </Button>
        </OptionsEditorGroup>
      </OptionsEditorColumn>
    </OptionsEditorGrid>
  );
}
